import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';

import { getConfig } from '../api/config';
import { getMe } from '../api/users';
import {
  clearTokens,
  decodeIdTokenClaims,
  getAccessTokenExpiry,
  refreshTokens,
  STORAGE_KEYS,
} from './oidc';

export type UserRole = 'admin' | 'developer' | 'public';

export interface AuthState {
  accessToken: string | null;
  role: UserRole;
  email: string | null;
  /** Display name from the IdP's `name` claim ("Firstname Lastname") — never
   *  the login/username claim. Null when the IdP provides none. */
  name: string | null;
  loading: boolean;
}

export interface AuthContextValue extends AuthState {
  logout: () => void;
  refreshAuth: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  accessToken: null,
  role: 'public',
  email: null,
  name: null,
  loading: true,
  logout: () => undefined,
  refreshAuth: () => undefined,
});

// ── Dev auth bypass ────────────────────────────────────────────────────────
// Set VITE_DEV_BYPASS_ROLE=admin|developer|public in .env.local to skip the
// OIDC PKCE flow entirely. Gated on the dev server (import.meta.env.DEV is a
// build-time constant) so `yarn build` dead-code-eliminates the bypass —
// a production bundle can never carry it, even when .env.local sets the var.
const DEV_BYPASS_ROLE = import.meta.env.DEV
  ? (import.meta.env.VITE_DEV_BYPASS_ROLE as UserRole | undefined)
  : undefined;

/**
 * Builds a minimal fake JWT understood by both the local dev server and the
 * deployed Lambda backend:
 *   - `role`            — read by dev-server's isDevOrAdmin()
 *   - `cognito:groups`  — read by the Lambda getRoleFromEvent() / determineRole()
 */
function makeDevBypassToken(role: UserRole): string {
  const groupMap: Record<UserRole, string[]> = {
    admin: ['admins'],
    developer: ['developers'],
    public: [],
  };
  const payload = btoa(
    JSON.stringify({
      sub: 'dev-bypass',
      email: 'dev@local',
      role,
      'cognito:groups': groupMap[role],
    }),
  );
  return `dev-header.${payload}.dev-sig`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    accessToken: null,
    role: 'public',
    email: null,
    name: null,
    loading: true,
  });

  const readFromStorage = useCallback(() => {
    const accessToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const idToken = sessionStorage.getItem(STORAGE_KEYS.ID_TOKEN);
    const claims = idToken ? decodeIdTokenClaims(idToken) : null;
    const email = (claims?.email as string) ?? null;
    // Display name: the OIDC `name` claim only — never preferred_username,
    // which is the IdP login name.
    const name = typeof claims?.name === 'string' ? claims.name : null;

    if (!accessToken) {
      setState({ accessToken: null, role: 'public', email: null, name: null, loading: false });
      return;
    }

    getMe()
      .then((me) => {
        setState({
          accessToken,
          role: me.role,
          email: me.email ?? email,
          name: me.name ?? name,
          loading: false,
        });
      })
      .catch(() => {
        // A token the backend won't honour is a dead session (e.g. pre-cutover
        // Cognito tokens still in sessionStorage after the IdP switch). Clear
        // it and show signed-out — never derive a role from unverified claims.
        clearTokens();
        setState({ accessToken: null, role: 'public', email: null, name: null, loading: false });
      });
  }, []);

  useEffect(() => {
    if (DEV_BYPASS_ROLE) {
      const token = makeDevBypassToken(DEV_BYPASS_ROLE);
      // apiFetch reads from sessionStorage, not from React state, so the token
      // must be stored there too for API calls to include the Authorization header.
      sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      setState({
        accessToken: token,
        role: DEV_BYPASS_ROLE,
        email: 'dev@local',
        name: null,
        loading: false,
      });
      return;
    }
    readFromStorage();
  }, [readFromStorage]);

  const logout = useCallback(() => {
    clearTokens();
    setState({ accessToken: null, role: 'public', email: null, name: null, loading: false });
  }, []);

  // Proactive session renewal: refresh the access token shortly before it
  // expires (immediately if already inside the window). A rejected refresh
  // grant means the session is truly over → sign out. Transient errors (e.g.
  // config fetch failed) leave the current token in place until expiry.
  useEffect(() => {
    if (DEV_BYPASS_ROLE || !state.accessToken) {
      return;
    }
    if (!sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
      return;
    }
    const expiry = getAccessTokenExpiry();
    if (!expiry) {
      return;
    }
    const delay = Math.max(expiry - Date.now() - 5 * 60_000, 0);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const refreshed = await refreshTokens(await getConfig());
          if (refreshed) {
            readFromStorage(); // new token → this effect re-arms for the next expiry
          } else {
            logout();
          }
        } catch {
          // transient — keep the session until the token actually expires
        }
      })();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [state.accessToken, readFromStorage, logout]);

  return (
    <AuthContext.Provider value={{ ...state, logout, refreshAuth: readFromStorage }}>
      {children}
    </AuthContext.Provider>
  );
}
